/* eslint-disable camelcase */
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ActionType,
  IMachine,
  MachineType,
  PROCESS_DROPDOWN_OPTIONS,
} from '../../../../PcfWizard.type';
import RemoveProcessButton from '../../RemoveProcessButton';
import AuxiliaryMachineForm from '../auxiliary-machine-form/AuxiliaryMachineForm';
import InputMaterial from '../input-material/InputMaterial';
import WasteManagementForm from './components/waste-management-form/WasteManagementForm';
import InjectionMoldingMainMachineForm from './InjectionMoldingMainMachineForm';

const MAX_AUXILLARY_MACHINES = 10;

interface InjectionMoldingFormProps {
  name: string;
  processIndex: number;
  onDelete: (showConfirmationDialog: boolean) => void;
}

const InjectionMoldingForm = ({
  name,
  processIndex,
  onDelete,
}: InjectionMoldingFormProps): JSX.Element => {
  const { control, setValue, getValues } = useFormContext();
  const machines = useWatch({
    name: `processes[${processIndex}].machines`,
    control,
  });
  const { t } = useTranslation();
  const { fields } = useFieldArray({
    control,
    name: `${name}[${processIndex}].machines`,
  });

  const auxillaryMachines = useMemo(() => {
    return machines.filter((item: IMachine) => {
      return item.type === MachineType.AUXILIARY;
    });
  }, [machines, processIndex]);

  const onAddAnotherMachine = () => {
    const newMachines = [...machines];
    newMachines.push({
      type: MachineType.AUXILIARY,
      action: ActionType.ADD,
      name: '',
      operation_mode: null,
      rated_power: null,
      operation_value: null,
    });
    setValue(`${name}[${processIndex}].machines`, newMachines);
  };

  return (
    <div>
      {/* Header */}
      <div className='flex flex-row w-full justify-content-between align-items-center pb-4'>
        <h2 className='text-center m-0 font-medium' style={{ fontSize: '20px' }}>
          {getValues(`${name}[${processIndex}].name`)}
        </h2>
        <RemoveProcessButton
          onClick={() => {
            return onDelete(true);
          }}
        />
      </div>

      {/* Input Material Section
        - Currently only one input material is allowed but in future we might allow multiple
      */}
      <InputMaterial
        formPath={`${name}[${processIndex}].input_materials[0]`}
        processIndex={processIndex}
        processType={PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING}
      />

      {/* Machine Section */}
      <div className='mb-2 '>
        {fields.map((item, index) => {
          const machine = getValues(`${name}[${processIndex}].machines[${index}]`) as IMachine;

          return (
            <div key={index}>
              {machine?.type === MachineType.MAIN && (
                <div className='surface-ground border-round-2xl p-3 mb-3'>
                  {/* Machine Title */}
                  <h3 className='font-medium mb-1' style={{ fontSize: '16px' }}>
                    {t(
                      'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.title_machine',
                    )}
                  </h3>

                  {/* Machine Info Message */}
                  <Message
                    severity='info'
                    className='bg-transparent p-0'
                    content={
                      <p className='flex flex-row w-full text-600' style={{ fontSize: '12px' }}>
                        {t(
                          'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.guidanceMessage',
                        )}
                      </p>
                    }
                  />

                  {/* Inputs for machine */}
                  <h4 className='font-medium mb-3' style={{ fontSize: '14px' }}>
                    {t('footprintDetailsPage.sections.calculatePcfSection.machineSection.title')}
                  </h4>
                  <InjectionMoldingMainMachineForm
                    processIndex={processIndex}
                    machineIndex={index}
                  />
                </div>
              )}

              {machine?.type === MachineType.AUXILIARY && (
                <AuxiliaryMachineForm
                  processIndex={processIndex}
                  machineIndex={index}
                  key={item.id}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Add Another Machine button */}
      {auxillaryMachines?.length < MAX_AUXILLARY_MACHINES && (
        <div className='flex p-ai-center p-mb-3'>
          <Button
            label={t(
              'footprintDetailsPage.sections.calculatePcfSection.machineSection.addAnotherMachine',
            )}
            icon='pi pi-plus-circle'
            className='p-button-text p-button-warning'
            onClick={onAddAnotherMachine}
          />
        </div>
      )}

      {/* Waste Management Section */}
      <WasteManagementForm processIndex={processIndex} />
    </div>
  );
};

export default InjectionMoldingForm;
